import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import moment from "moment-timezone";
import Spinner from "../../../Spinner";
import Recurring from "./Recurring";
import Temporary from "./Temporary";
import useApi from "../../../../../utils/api";
import { cn } from "../../../../../lib/utils";

const tabs = ["recurring", "temporary"] as const;
type Tab = typeof tabs[number];

function Availability({ user }: any) {
    const { t } = useTranslation();
    const [menu, setMenu] = useState<Tab>("recurring");
    const [filters, setFilters] = useState({
        mode: "week",
        from: moment().startOf("week").format("YYYY-MM-DD"),
        to: moment().endOf("week").format("YYYY-MM-DD")
    });
    const { authGet } = useApi();

    const params = useMemo(() => {
        if (menu === "recurring") {
            return { type: "recurring" };
        } else {
            return {
                type: "temporary",
                startDate: moment(filters?.from).format("YYYY-MM-DD"),
                endDate: moment(filters?.to).format("YYYY-MM-DD")
            };
        }
    }, [menu, filters?.from, filters?.to]);

    const query = useQuery({
        queryKey: [`/users/${user?.id}/availability`, params],
        queryFn: ({ queryKey }: any) => {
            const [url, params] = queryKey;
            return authGet(url, { params });
        },
        enabled: !!user?.id,
        placeholderData: keepPreviousData
    });

    const content = useMemo(() => {
        if (menu === "recurring") {
            return <Recurring query={query} />;
        } else {
            return (
                <Temporary
                    {...filters}
                    setFilters={setFilters}
                    query={query}
                />
            );
        }
    }, [query, menu, filters, setFilters]);

    return (
        <div className="tw-border tw-rounded-xl tw-overflow-hidden tw-flex tw-flex-col">
            <div className="tw-flex tw-border-b">
                {tabs.map((tab) => (
                    <button
                        key={tab}
                        type="button"
                        onClick={() => setMenu(tab)}
                        className={cn(
                            "tw-px-5 tw-py-3 tw-text-sm tw-font-medium tw-border-b-2 tw--mb-px tw-transition-colors tw-cursor-pointer",
                            menu === tab
                                ? "tw-border-primary tw-text-foreground"
                                : "tw-border-transparent tw-text-muted-foreground hover:tw-text-foreground hover:tw-border-muted-foreground"
                        )}
                    >
                        {t(tab)}
                    </button>
                ))}
            </div>

            <div className="tw-p-4">
                {query.isLoading ? (
                    <div className="tw-flex tw-items-center tw-justify-center tw-py-7">
                        <Spinner className="tw-text-primary" />
                    </div>
                ) : content}
            </div>
        </div>
    );
}

export default Availability;
